// =====================================================================
// Element motion
// =====================================================================
// One description of how a logo or a line of text moves on and off
// screen. `motionFrame` evaluates it for the browser preview and
// `motionClipProps` turns it into Shotstack keyframe tweens, both from
// the same start state and easing so the preview matches the export.
//
// Offsets use Shotstack space: fractions of the frame, positive y is up.
// =====================================================================

export type MotionStyle =
  | 'none'
  | 'fade'
  | 'grow'
  | 'rise'
  | 'drift'
  | 'slide'
  | 'bungee'
  | 'tumble'
  | 'spin'
  | 'wipe';

export type MotionPersonality = 'soft' | 'smooth' | 'strong' | 'energetic' | 'loud';
export type MotionDirection = 'up' | 'down' | 'left' | 'right';

export interface ElementMotion {
  style: MotionStyle;
  /** Seconds the entrance takes. The exit, when on, mirrors it. */
  duration: number;
  personality: MotionPersonality;
  fade: boolean;
  /** Direction of travel for styles that move. */
  direction: MotionDirection;
  /** Travel in from beyond the edge of the frame. */
  startOutside: boolean;
  /** Play the entrance in reverse before the element leaves. */
  exit: boolean;
}

export const MIN_MOTION_SECONDS = 0.2;
export const MAX_MOTION_SECONDS = 4;

export const DEFAULT_MOTION: ElementMotion = {
  style: 'fade',
  duration: 0.9,
  personality: 'smooth',
  fade: true,
  direction: 'up',
  startOutside: false,
  exit: false,
};

export interface MotionStyleOption {
  value: MotionStyle;
  label: string;
  hint: string;
  /** Which directions the user can pick. Empty = fixed. */
  directions: MotionDirection[];
  /** Whether "start outside the frame" applies. */
  travels: boolean;
}

const ALL_DIRECTIONS: MotionDirection[] = ['up', 'down', 'left', 'right'];

export const MOTION_STYLES: MotionStyleOption[] = [
  { value: 'none', label: 'None', hint: 'Appears with no animation', directions: [], travels: false },
  { value: 'fade', label: 'Fade', hint: 'Fades up gently', directions: [], travels: false },
  { value: 'grow', label: 'Grow', hint: 'Scales up into place', directions: [], travels: false },
  { value: 'rise', label: 'Rise', hint: 'Lifts up into place', directions: [], travels: true },
  { value: 'drift', label: 'Drift', hint: 'Glides a short way into place', directions: ALL_DIRECTIONS, travels: true },
  { value: 'slide', label: 'Slide', hint: 'Slides in from one side', directions: ALL_DIRECTIONS, travels: true },
  { value: 'bungee', label: 'Bungee', hint: 'Drops in and bounces', directions: [], travels: true },
  { value: 'tumble', label: 'Tumble', hint: 'Rolls in from the side', directions: ['left', 'right'], travels: true },
  { value: 'spin', label: 'Spin', hint: 'Turns into place', directions: ['left', 'right'], travels: false },
  { value: 'wipe', label: 'Wipe', hint: 'Revealed from left to right', directions: [], travels: false },
];

export const MOTION_PERSONALITIES: Array<{ value: MotionPersonality; label: string }> = [
  { value: 'soft', label: 'Soft' },
  { value: 'smooth', label: 'Smooth' },
  { value: 'strong', label: 'Strong' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'loud', label: 'Loud' },
];

type Easing =
  | 'easeOutSine'
  | 'easeInSine'
  | 'easeInOutCubic'
  | 'easeOutQuart'
  | 'easeInQuart'
  | 'easeOutBack'
  | 'easeInBack';

const PERSONALITY: Record<MotionPersonality, { ease: Easing; amount: number }> = {
  soft: { ease: 'easeOutSine', amount: 0.5 },
  smooth: { ease: 'easeInOutCubic', amount: 0.75 },
  strong: { ease: 'easeOutQuart', amount: 1 },
  energetic: { ease: 'easeOutBack', amount: 1.2 },
  loud: { ease: 'easeOutBack', amount: 1.6 },
};

const MIRROR: Record<Easing, Easing> = {
  easeOutSine: 'easeInSine',
  easeInSine: 'easeOutSine',
  easeInOutCubic: 'easeInOutCubic',
  easeOutQuart: 'easeInQuart',
  easeInQuart: 'easeOutQuart',
  easeOutBack: 'easeInBack',
  easeInBack: 'easeOutBack',
};

const BACK = 1.70158;

function ease(name: Easing, t: number): number {
  switch (name) {
    case 'easeOutSine':
      return Math.sin((t * Math.PI) / 2);
    case 'easeInSine':
      return 1 - Math.cos((t * Math.PI) / 2);
    case 'easeInOutCubic':
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    case 'easeOutQuart':
      return 1 - Math.pow(1 - t, 4);
    case 'easeInQuart':
      return t * t * t * t;
    case 'easeOutBack':
      return 1 + (BACK + 1) * Math.pow(t - 1, 3) + BACK * Math.pow(t - 1, 2);
    case 'easeInBack':
      return (BACK + 1) * t * t * t - BACK * t * t;
  }
}

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

const STYLE_VALUES = new Set(MOTION_STYLES.map((s) => s.value));
const PERSONALITY_VALUES = new Set(MOTION_PERSONALITIES.map((p) => p.value));
const DIRECTION_VALUES = new Set<string>(ALL_DIRECTIONS);

/** Fill in and clamp a saved motion so old or hand-edited documents stay valid. */
export function normaliseMotion(raw: unknown, fallback: ElementMotion = DEFAULT_MOTION): ElementMotion {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const m = raw as Partial<ElementMotion>;
  return {
    style: m.style && STYLE_VALUES.has(m.style) ? m.style : fallback.style,
    duration: Math.round(clamp(Number(m.duration ?? fallback.duration), MIN_MOTION_SECONDS, MAX_MOTION_SECONDS) * 100) / 100,
    personality: m.personality && PERSONALITY_VALUES.has(m.personality) ? m.personality : fallback.personality,
    fade: typeof m.fade === 'boolean' ? m.fade : fallback.fade,
    direction: m.direction && DIRECTION_VALUES.has(m.direction) ? m.direction : fallback.direction,
    startOutside: typeof m.startOutside === 'boolean' ? m.startOutside : fallback.startOutside,
    exit: typeof m.exit === 'boolean' ? m.exit : fallback.exit,
  };
}

/** Map the original plate logo choices onto the motion model. */
export function motionFromLogoAnimation(style: string | undefined): ElementMotion {
  if (style === 'none') return { ...DEFAULT_MOTION, style: 'none' };
  if (style === 'zoomIn') return { ...DEFAULT_MOTION, style: 'grow' };
  if (style === 'slideUp') return { ...DEFAULT_MOTION, style: 'rise' };
  if (style === 'reveal') return { ...DEFAULT_MOTION, style: 'wipe', fade: false };
  return { ...DEFAULT_MOTION };
}

/** Pose relative to the element's resting position. */
interface Pose {
  opacity: number;
  dx: number;
  dy: number;
  rotate: number;
  scale: number;
}

const REST: Pose = { opacity: 1, dx: 0, dy: 0, rotate: 0, scale: 1 };
const OUTSIDE = 1.3;

function travelVector(direction: MotionDirection): { x: number; y: number } {
  if (direction === 'up') return { x: 0, y: 1 };
  if (direction === 'down') return { x: 0, y: -1 };
  if (direction === 'left') return { x: -1, y: 0 };
  return { x: 1, y: 0 };
}

function easingFor(motion: ElementMotion): Easing {
  if (motion.style === 'bungee') return 'easeOutBack';
  return PERSONALITY[motion.personality].ease;
}

/** Where the element starts its entrance (and ends its exit). */
function startPose(motion: ElementMotion): Pose {
  const a = PERSONALITY[motion.personality].amount;
  const pose: Pose = { ...REST };
  if (motion.fade || motion.style === 'fade') pose.opacity = 0;
  const travel = (distance: number, direction: MotionDirection) => {
    const v = travelVector(direction);
    const d = motion.startOutside ? OUTSIDE : distance;
    pose.dx = -v.x * d;
    pose.dy = -v.y * d;
  };
  switch (motion.style) {
    case 'grow':
      pose.scale = Math.max(0.05, 1 - 0.7 * a);
      break;
    case 'rise':
      travel(0.08 * a, 'up');
      break;
    case 'drift':
      travel(0.05 * a, motion.direction);
      break;
    case 'slide':
      travel(0.25 * a, motion.direction);
      break;
    case 'bungee':
      travel(0.2 * a, 'down');
      break;
    case 'tumble': {
      const direction = motion.direction === 'left' ? 'left' : 'right';
      travel(0.2 * a, direction);
      pose.rotate = (direction === 'right' ? -1 : 1) * 90 * a;
      break;
    }
    case 'spin':
      pose.rotate = (motion.direction === 'left' ? 1 : -1) * 180 * a;
      pose.scale = Math.max(0.05, 1 - 0.4 * a);
      break;
    case 'wipe':
      pose.opacity = 1;
      break;
  }
  return pose;
}

/** Entrance length after fitting it into the element's time on screen. */
function fittedDuration(motion: ElementMotion, length: number): number {
  const room = motion.exit ? length / 2 : length;
  return Math.max(0.05, Math.min(clamp(motion.duration, MIN_MOTION_SECONDS, MAX_MOTION_SECONDS), room));
}

export interface MotionFrame extends Pose {
  /** 0–1 share of the element revealed from the left. 1 when not wiping. */
  reveal: number;
}

function mix(from: Pose, to: Pose, p: number): Pose {
  return {
    opacity: clamp(from.opacity + (to.opacity - from.opacity) * p, 0, 1),
    dx: from.dx + (to.dx - from.dx) * p,
    dy: from.dy + (to.dy - from.dy) * p,
    rotate: from.rotate + (to.rotate - from.rotate) * p,
    scale: Math.max(0, from.scale + (to.scale - from.scale) * p),
  };
}

/** The pose `local` seconds after the element appears. */
export function motionFrame(motion: ElementMotion | undefined, local: number, length: number): MotionFrame {
  if (!motion || motion.style === 'none' || length <= 0) return { ...REST, reveal: 1 };
  const d = fittedDuration(motion, length);
  const easing = easingFor(motion);
  const from = startPose(motion);
  const wipe = motion.style === 'wipe';
  if (motion.exit && local > length - d) {
    const q = ease(MIRROR[easing], clamp((local - (length - d)) / d, 0, 1));
    return { ...mix(REST, from, q), reveal: wipe ? 1 - clamp((local - (length - d)) / d, 0, 1) : 1 };
  }
  const t = clamp(local / d, 0, 1);
  return { ...mix(from, REST, ease(easing, t)), reveal: wipe ? t : 1 };
}

interface Tween {
  from: number;
  to: number;
  start: number;
  length: number;
  interpolation: 'bezier';
  easing: Easing;
}

function round(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function tweens(from: number, rest: number, length: number, d: number, easing: Easing, exit: boolean): Tween[] {
  const list: Tween[] = [
    { from: round(from), to: round(rest), start: 0, length: round(d), interpolation: 'bezier', easing },
  ];
  if (exit) {
    list.push({
      from: round(rest),
      to: round(from),
      start: round(length - d),
      length: round(d),
      interpolation: 'bezier',
      easing: MIRROR[easing],
    });
  }
  return list;
}

function revealSpeed(seconds: number): string {
  if (seconds <= 0.6) return 'revealFast';
  if (seconds >= 1.5) return 'revealSlow';
  return 'reveal';
}

/**
 * Shotstack clip properties for a motion. Spread over a clip; returns the
 * static `offset` / `scale` when nothing animates so callers can always
 * use the result in place of their own values.
 */
export function motionClipProps(
  motion: ElementMotion | undefined,
  length: number,
  base: { x: number; y: number; scale?: number; opacity?: number },
): Record<string, any> {
  const baseScale = base.scale ?? 1;
  const baseOpacity = base.opacity ?? 1;
  const props: Record<string, any> = {};
  const staticOffset = base.x !== 0 || base.y !== 0 ? { x: round(base.x), y: round(base.y) } : undefined;
  if (base.scale != null) props.scale = baseScale;
  if (staticOffset) props.offset = staticOffset;
  if (baseOpacity !== 1) props.opacity = round(baseOpacity);
  if (!motion || motion.style === 'none' || length <= 0) return props;

  const d = fittedDuration(motion, length);
  if (motion.style === 'wipe') {
    const name = revealSpeed(d);
    props.transition = motion.exit ? { in: name, out: name } : { in: name };
    return props;
  }

  const easing = easingFor(motion);
  const from = startPose(motion);
  if (from.opacity !== 1) {
    props.opacity = tweens(baseOpacity * from.opacity, baseOpacity, length, d, easing, motion.exit);
  }
  if (from.scale !== 1) props.scale = tweens(baseScale * from.scale, baseScale, length, d, easing, motion.exit);
  if (from.dx !== 0 || from.dy !== 0) {
    props.offset = {
      x: from.dx !== 0 ? tweens(base.x + from.dx, base.x, length, d, easing, motion.exit) : round(base.x),
      y: from.dy !== 0 ? tweens(base.y + from.dy, base.y, length, d, easing, motion.exit) : round(base.y),
    };
  }
  if (from.rotate !== 0) {
    props.transform = { rotate: { angle: tweens(from.rotate, 0, length, d, easing, motion.exit) } };
  }
  return props;
}
